import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {repeat} from 'lit/directives/repeat.js';
import {when} from 'lit/directives/when.js';
import {classMap} from 'lit/directives/class-map.js';
import {AttributeInterpreter} from '@viamedici-spc/configurator-ts';
import store from '../store';
import {featureInSection} from '../util/featureInSection';
import {
	selectCurrentSection, selectFilteredSpcAttributes,
	selectLuminary,
	selectProductData,
	selectSectionConfig,
	selectSpcAttributes, selectUnFilteredSpcAttributes, selectUnsatisfiedSpcAttributes, selectUserLoggedIn,
} from '../selectors';
import {selectSolarLumPosition2IsVisible} from '../solar/solarSelectors';
import {luminaryOptions} from '../lightProfile/lightProfileSectionConfig';
import {generalAttributeOptions} from '../generalAttributeOptions';

@customElement('feature-container')
export class FeatureContainer extends connect(store)(LitElement) {

	static styles = css`
		:host {
			height: 100%;
			width: 100%;
		}

		.container {
			width: var(--features-column-width);
			overflow-y: scroll;
			padding: 0 calc(100vw - var(--features-column-width) - var(--large-padding)) 0 var(--large-padding);
			pointer-events: none;
			height: 100%;
			overflow-x: hidden;
		}

		.container.columns {
			//columns: var(--features-column-width) 6;
			//column-fill: balance;
			display: grid;
			grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
			grid-template-rows: 1fr;
			grid-column-gap: 32px;
			grid-row-gap: 0;
			width: 100%;
			box-sizing: border-box;
			padding: 0 var(--large-padding) 0 var(--large-padding);
		}

		// for Opera, Chrome, Edge, and Safari, Firefox

		:host::-webkit-scrollbar {
			display: none;
		}

		@media (max-width: 768px) {
			:host {
				align-self: flex-start;
				padding-top: var(--large-padding);
				padding-left: var(--small-padding);
				padding-right: var(--small-padding);
			}

			.container {
				width: 100%;
				padding-left: 0;
				padding-right: 0;
			}
		}

		.features {
			break-inside: avoid;
			padding-bottom: var(--large-space);
			width: 100%;
			pointer-events: none;
			display: none;
			flex-flow: column;
			place-content: flex-start;
			gap: var(--large-space);
			margin-bottom: 90px;
		}
		.breakInside {
			break-inside: auto;
		}
		.features:has(> *) {
			pointer-events: all;
		}

		.header {
			font: var(--font-widget-label);
			color: var(--color-grey4);
			padding-bottom: var(--small-padding);
			border-bottom: 1px solid var(--color-white);
			display: none;
		}

		.dashboard .header {
			display: block;
		}

		.features > * {
			pointer-events: auto;
		}

		@media (max-width: 768px) {
			:host {
				padding: 0 var(--small-padding) var(--small-padding);
			}

			.features {
				min-width: calc(100vw - var(--small-padding) * 2);
				padding-bottom: 90px;
			}
		}

		.active {
			display: flex;
			pointer-events: all
		}

		.dashboard {
			display: flex;
			pointer-events: all
		}
		.unsatisfied {
			position: absolute;
			bottom: 0;
			color: white;
			padding: 30px;
			font-size: 12px;
			margin-left: 420px;
			font-family: 'Lineto Brown Pro Light';
		}
	`;

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.translations = state.productData.translations;
		this.productData = selectProductData(this.state);
		this.sectionConfig = selectSectionConfig(state);
		this.region = state.status.region;
		this.lang = state.status.lang;
		this.currentSection = selectCurrentSection(state);
		this.dashboardActive = state.status.dashboardActive;
		this.isLightProfile = state.status.family === 'LightProfile';
		this.isSolar = state.status.family === 'Aeto';
		this.internal = state.status.mode === 'internal';

		// this.spcConfiguration = selectSpcConfiguration(state);
		this.spcAttributes = selectSpcAttributes(state) ?? [];
		//console.log('spcAttributes', this.spcAttributes);
		this.debugShowAll = state.status.debugShowAll;
	}
	isSatisfied(spcAttribute) {
		return spcAttribute.isSatisfied;
	}
	canBeOmitted(spcAttribute) {
		//console.log(spcAttribute.key, spcAttribute.isSatisfied, AttributeInterpreter.getAllowedChoiceValues(spcAttribute));
		// satisfied and no value available
		return spcAttribute.isSatisfied && AttributeInterpreter.getAllowedChoiceValues(spcAttribute).length < 1;
	}
	render() {
		// if (!this.spcAttributes) return null;
		return html`
			${when(this.debugShowAll && selectUnsatisfiedSpcAttributes(this.state) && selectUnsatisfiedSpcAttributes(this.state).length > 0, () => html`
			<div class="unsatisfied">Unsatisfied:
				${repeat(selectUnsatisfiedSpcAttributes(this.state),
					(spcAttribute) => html`
						<span>${spcAttribute.key},</span>
					`)}
			</div>
			`)}
			<div class="container ${classMap({columns: this.dashboardActive})}">
				${repeat(
					this.sectionConfig,
					(section) => html`
						${when((this.dashboardActive && section.notInDashboard !== true) || (!this.dashboardActive && section.id === this.currentSection), () => html`
							<div class="features ${classMap({active: !this.dashboardActive && section.id === this.currentSection, dashboard: this.dashboardActive})}">
								${when(section.label, () => html`<div class="header">${section.label[this.lang]}</div>`)}
								${(this.dashboardActive || section.id === this.currentSection) ? html`
									${section.widgets}
									${this.isLightProfile && section.id === 'luminaire' ? luminaryOptions.find(luminary => luminary.id === selectLuminary(this.state))?.widgets : null}
									${repeat(
										this.spcAttributes
											// special case for eato: remove second lum settings when not selected
											.filter(spcAttribute => !(generalAttributeOptions?.[spcAttribute.key]?.requireLogin ?? false) || selectUserLoggedIn(this.state))
											.filter(spcAttribute => !(this.isSolar && !selectSolarLumPosition2IsVisible(this.state) && spcAttribute.key === 'root::LightEngine_2::LE_LightDistribution'))
											.filter(spcAttribute => !(this.isSolar && !selectSolarLumPosition2IsVisible(this.state) && spcAttribute.key === 'root::LE_LuminairePositionHeightSU_2'))
											.filter(spcAttribute => this.debugShowAll || !this.isLightProfile || luminaryOptions.find(luminary => luminary.id === selectLuminary(this.state))?.features.includes(spcAttribute.key))
											.filter(spcAttribute => featureInSection(this.sectionConfig, section, spcAttribute.key))
											// order by occurrence in sectionConfig Array
											.sort((a, b) => {
												const indexA = section.features.findIndex(feature => feature === a.key);
												const indexB = section.features.findIndex(feature => feature === b.key);
												return indexA - indexB;
											})
											.filter(spcAttribute => !this.canBeOmitted(spcAttribute)),

										(spcAttribute) => spcAttribute.key,
										(spcAttribute, index) => html`<spc-attribute .attribute=${spcAttribute}></spc-attribute>`
											// 'colorSelect','compactSelect', 'comboSelect', 'reducedSelect', 'reducedChoiceSelect'
										)
									}
									${section.widgetsEnd}
								` : null}
							</div>
						`)}
					`)
				}
			</div>
		`;
	}
}
